/// <amd-module name="Agile/Directory/Components/DirectoryGrid/DirectoryGrid" />
import "VSS/LoaderPlugins/Css!Agile/Scripts/Common/Directory/Components/DirectoryGrid";

import { NameCell } from "Agile/Directory/Components/DirectoryGrid/NameCell";
import { DirectoryColumnKeys, DirectoryCSS } from "Agile/Directory/Components/DirectoryGrid/DirectoryGridConstants";
import { GroupRow } from "Agile/Directory/Components/DirectoryGrid/GroupRow";
import { LoadingRow } from "Agile/Directory/Components/DirectoryGrid/LoadingRow";
import { TeamCell } from "Agile/Directory/Components/DirectoryGrid/TeamCell";
import { IDirectoryArtifact } from "Agile/Directory/DirectoryContracts";
import * as DirectoryResources from "Agile/Directory/Resources";
import { IContextualMenuItem } from "OfficeFabric/ContextualMenu";
import {
    ColumnActionsMode,
    ConstrainMode,
    DetailsListLayoutMode,
    DetailsRow,
    IColumn,
    IDetailsRowProps,
    SelectionMode
} from "OfficeFabric/DetailsList";
import { css } from "VSSUI/Util";
import * as React from "react";
import { IProjectData } from "Tfs/Platform/Page";
import { VssDetailsList } from "VSSUI/Components/VssDetailsList/VssDetailsList";
import { IDirectoryRow, IGridRow, IGroupRow, IZeroDataRow, ILoadingRow } from "Agile/Directory/Components/DirectoryGrid/DirectoryGrid.Props";

export const DEFAULT_NAME_COLUMN: IColumn = {
    fieldName: DirectoryColumnKeys.Name,
    key: DirectoryColumnKeys.Name,
    name: DirectoryResources.NameColumnHeader,
    minWidth: 400,
    maxWidth: 600,
    headerClassName: DirectoryCSS.DIRECTORY_COLUMN_HEADER,
    className: DirectoryCSS.DIRECTORY_COLUMN,
    isResizable: true,
    columnActionsMode: ColumnActionsMode.disabled
};

export const DEFAULT_TEAM_COLUMN: IColumn = {
    fieldName: DirectoryColumnKeys.Team,
    key: DirectoryColumnKeys.Team,
    name: DirectoryResources.TeamColumnHeader,
    isResizable: true,
    minWidth: 200,
    maxWidth: 600,
    headerClassName: DirectoryCSS.DIRECTORY_COLUMN_HEADER,
    className: DirectoryCSS.DIRECTORY_COLUMN,
    columnActionsMode: ColumnActionsMode.disabled
};

export interface IDirectoryGridProps<T extends IDirectoryArtifact> {
    project: IProjectData;
    /** Optional class name to append to the Grid container */
    className?: string;
    /** Get context menu items for the item */
    getContextMenuItems?: (item: IDirectoryRow<T>) => IContextualMenuItem[];
    /** Get the content for a zero data row for a group */
    getZeroDataContent?: (groupKey: string) => { content: JSX.Element | string; ariaLabel?: string };
    /** The groups to display */
    rows: IGridRow<T>[];
    isGrouped: boolean;
    /** The columns to display */
    columns: IColumn[];
    /** Optional render override */
    onRenderCell?: (
        item: IDirectoryRow<T>,
        index: number,
        column: IColumn,
        defaultRenderer?: (item: IDirectoryRow<T>, index: number, column: IColumn) => JSX.Element
    ) => JSX.Element;

    /** Handler which should render a zero data row for a group */
    onRenderZeroDataRow?: (groupKey: string) => JSX.Element | undefined;

    /** Called when the favorite star is toggled for a row */
    onFavoriteToggled?: (item: IDirectoryRow<T>) => void;
    /** Called when a group is expanded/collapsed */
    onGroupToggled?: (groupKey: string, collapsed: boolean) => void;
    /** Called when an item link is clicked/activated */
    onItemClicked?: (item: IDirectoryRow<T>, e?: Event) => void;
    /** Indicates if the grid should acquire focus when it mounts */
    takeFocusOnMount?: boolean;
}

/**
 * A generic directory grid component
 * @param T The type of the underlying artifact (ex: Team)
 * @param R The type of the row object, which extends IDirectoryRow<T>
 */
export class DirectoryGrid<T extends IDirectoryArtifact> extends React.Component<IDirectoryGridProps<T>> {
    private _hasMounted: boolean;

    public render(): JSX.Element {
        const { className, columns, rows, getContextMenuItems, takeFocusOnMount } = this.props;

        return (
            <div className={css("new-directory-grid-container", className)}>
                <VssDetailsList
                    setKey={"sprints-gridview"} // setKey is required to keep focus on the selected item when the row is re-rendered
                    layoutMode={DetailsListLayoutMode.justified}
                    constrainMode={ConstrainMode.unconstrained}
                    isHeaderVisible={true}
                    columns={columns}
                    className="directory-gridview"
                    items={rows}
                    listProps={{
                        getPageSpecification: this._getPageSpecification
                    }}
                    onRenderItemColumn={this._onRenderCell}
                    selectionMode={SelectionMode.single}
                    initialFocusedIndex={takeFocusOnMount && !this._hasMounted ? 0 : -1}
                    getKey={this._getRowKey}
                    onRenderRow={this._onRenderRow}
                    selectionPreservedOnEmptyClick={true}
                    allocateSpaceForActionsButtonWhileHidden={true}
                    actionsColumnKey={DirectoryColumnKeys.Name}
                    getMenuItems={getContextMenuItems ? this._getContextMenuItems : undefined}
                    shouldDisplayActions={this._shouldDisplayActions}
                    onItemInvoked={this._onRowInvoked}
                />
            </div>
        );
    }

    public componentDidMount(): void {
        this._hasMounted = true;
    }

    private _onRenderRow = (props: IDetailsRowProps): JSX.Element => {
        const { columns, isGrouped, onRenderZeroDataRow } = this.props;

        if (this._isGroupRow(props.item)) {
            return (
                <GroupRow
                    columnKey={columns[0].key}
                    row={props.item as IGroupRow}
                    onToggled={this._onGroupRowToggled}
                    {...props}
                    // Shut up the typesystem complaining about refs
                    ref={null as any}
                    componentRef={null as any}
                />
            );
        } else if (this._isZeroDataRow(props.item)) {
            return (
                <ZeroDataRow
                    columnKey={columns[0].key}
                    isGrouped={isGrouped}
                    onRenderRowContent={onRenderZeroDataRow}
                    row={props.item as IZeroDataRow}
                    {...props}
                    // Shut up the typesystem complaining about refs
                    ref={null as any}
                    componentRef={null as any}
                />
            );
        } else if (this._isLoadingRow(props.item)) {
            return (
                <LoadingRow
                    columnKey={columns[0].key}
                    isGrouped={isGrouped}
                    row={props.item as ILoadingRow}
                    {...props}
                    // Shut up the typesystem complaining about refs
                    ref={null as any}
                    componentRef={null as any}
                />
            );
        } else {
            return <DetailsRow {...props} />;
        }
    };

    private _getPageSpecification = (index: number, visibleRect: any) => {
        const { isGrouped, rows } = this.props;

        const maxPageSize = 10;
        if (isGrouped) {
            const startRow = rows[index];
            if (startRow) {
                let itemsOnPage = 0;

                let currentGroupKey = this._getRowGroupKey(startRow);
                if (!currentGroupKey) {
                    // throw
                }

                const groupItems = rows.filter(r => this._getRowGroupKey(r) === currentGroupKey);
                const groupItemIndex = groupItems.findIndex(r => r.key === startRow.key);

                for (let i = index; itemsOnPage < maxPageSize && i < rows.length; i++) {
                    const row = rows[i];
                    const rowGroupKey = this._getRowGroupKey(row);
                    if (rowGroupKey !== currentGroupKey) {
                        // End page
                        break;
                    }

                    itemsOnPage++;
                }

                return {
                    itemCount: itemsOnPage,
                    height: itemsOnPage * 36,
                    data: null,
                    key: `p-${currentGroupKey}-${groupItemIndex}`
                };
            }
        }

        return {
            itemCount: maxPageSize,
            height: maxPageSize * 36,
            data: null,
            key: `p-${index}`
        };
    };

    private _onRenderCell = (row: IDirectoryRow<T>, index: number, column: IColumn): JSX.Element | null => {
        const { columns, isGrouped, onRenderCell = this._defaultOnRenderCell } = this.props;

        return (
            <div className={DirectoryCSS.DIRECTORY_ROWITEM}>
                {isGrouped && column.key === columns[0].key && <div className={DirectoryCSS.DIRECTORY_GROUP_SPACER} />}
                {onRenderCell(row, index, column, this._defaultOnRenderCell)}
            </div>
        );
    };

    private _getRowGroupKey(row: IGridRow<T>): string | undefined {
        if (this._isGroupRow(row)) {
            return (row as IGroupRow).key;
        }

        return (row as IDirectoryRow<T>).groupKey;
    }

    private _defaultOnRenderCell = (row: IDirectoryRow<T>, index: number, column: IColumn, __defaultRenderer: any): JSX.Element | null => {
        switch (column.fieldName) {
            case DirectoryColumnKeys.Name:
                return this._renderNameCell(row, index);
            case DirectoryColumnKeys.Team:
                return this._renderTeamCell(row);
            default:
                return null;
        }
    };

    /**
     * Render the name cell
     */
    private _renderNameCell(row: IDirectoryRow<T>, index: number): JSX.Element {
        return <NameCell row={row} onFavoriteToggled={this._onFavoriteToggled} onClick={this._onRowClicked} />;
    }

    /**
     * Render team cell
     */
    private _renderTeamCell(row: IDirectoryRow<T>): JSX.Element | null {
        if (row.data.owners && row.data.owners.length > 0) {
            return <TeamCell row={row} project={this.props.project} />;
        }

        return null;
    }

    private _getContextMenuItems = (row: IGridRow<T>): IContextualMenuItem[] => {
        const { getContextMenuItems } = this.props;

        if (getContextMenuItems && !this._isGroupRow(row)) {
            return getContextMenuItems(row as IDirectoryRow<T>);
        }

        return [];
    };

    private _onFavoriteToggled = (item: IDirectoryRow<T>): void => {
        const { onFavoriteToggled } = this.props;

        if (onFavoriteToggled) {
            onFavoriteToggled(item);
        }
    };

    private _onGroupRowToggled = (row: IGroupRow, collapsed: boolean): void => {
        const { onGroupToggled } = this.props;

        if (onGroupToggled) {
            onGroupToggled(row.key, collapsed);
        }
    };

    private _onRowClicked = (row: IDirectoryRow<T>, index?: number, ev?: Event): void => {
        const { onItemClicked } = this.props;

        if (onItemClicked) {
            onItemClicked(row, ev);
        }
    };

    private _onRowInvoked = (row: IGridRow<T>, index?: number, ev?: Event): void => {
        if (!this._isGroupRow(row)) {
            const directoryRow = row as IDirectoryRow<T>;
            if (!directoryRow.isDeleted) {
                this._onRowClicked(row as IDirectoryRow<T>, index, ev);
            }
        }
    };

    private _getRowKey = (row: IDirectoryRow<T>): string => {
        return row.key;
    };

    private _isZeroDataRow(row: IZeroDataRow): boolean {
        return row.hasOwnProperty("isZeroDataRow") && (row as IZeroDataRow).isZeroDataRow;
    }

    private _isGroupRow(row: IGridRow<T>): boolean {
        return row.hasOwnProperty("isGroupRow") && (row as IGroupRow).isGroupRow;
    }

    private _isLoadingRow(row: IGridRow<T>): boolean {
        return row.hasOwnProperty("isLoadingRow") && (row as ILoadingRow).isLoadingRow;
    }

    private _shouldDisplayActions = (row: IGridRow<T>): boolean => {
        const { getContextMenuItems } = this.props;

        if (!getContextMenuItems) {
            return false;
        }

        if (this._isGroupRow(row)) {
            return false;
        }

        return true;
    };
}

export interface IZeroDataRowProps extends IDetailsRowProps {
    columnKey: string;
    onRenderRowContent?: (rowKey: string) => JSX.Element | undefined;
    row: IZeroDataRow;
    isGrouped: boolean;
}
class ZeroDataRow extends React.PureComponent<IZeroDataRowProps> {
    public render(): JSX.Element {
        return <DetailsRow {...this.props} onRenderItemColumn={this._renderCell} />;
    }

    private _renderCell = (row: IZeroDataRow, index: number, column: IColumn): JSX.Element | undefined => {
        const { columnKey, onRenderRowContent, isGrouped } = this.props;

        if (column.key === columnKey) {
            return (
                <div className={DirectoryCSS.DIRECTORY_ROWITEM}>
                    {isGrouped && <div className={DirectoryCSS.DIRECTORY_GROUP_SPACER} />}
                    <div className="directory-row-title">{onRenderRowContent && onRenderRowContent(row.groupKey)}</div>
                </div>
            );
        }
    };
}
